import React, { useState, useEffect, useMemo } from 'react'
import { Autocomplete, TextField, CircularProgress, Box, Typography, Paper } from '@mui/material'
import { DataModelSchemaList } from 'common/types/kraken-core/DataModelSchema'
import { onSearchDataModelSchemas } from 'common/components/datasources/datasource'
import { Z_INDEX } from 'common/constants'
import ContentTypeTag from 'atoms/ContentTypeTag/ContentTypeTag'

export interface MUIDataModelSelectorProps {
  selected?: {
    label?: string
    id?: string
  }
  onSelect: (selected: DataModelSchemaList | null) => void
  disabled?: boolean
  placeholder?: string
  label?: string
  required?: boolean
  error?: boolean
  helperText?: string
}

export default function MUIDataModelSelector({
  selected,
  onSelect,
  disabled = false,
  placeholder = 'Select a Data Model Schema',
  label = 'Data Model Schema',
  required = false,
  error = false,
  helperText,
}: MUIDataModelSelectorProps) {
  const [dataModelSchemas, setDataModelSchemas] = useState<DataModelSchemaList[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [open, setOpen] = useState(false)

  // Load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      setIsLoading(true)
      try {
        const result = await onSearchDataModelSchemas({ query: '' })
        setDataModelSchemas(result || [])
      } catch (error) {
        console.error('Error loading data model schemas:', error)
      } finally {
        setIsLoading(false)
      }
    }

    loadInitialData()
  }, [])

  // Search when query changes
  useEffect(() => {
    if (!open) return

    const searchDataModelSchemas = async () => {
      setIsLoading(true)
      try {
        const result = await onSearchDataModelSchemas({ query: searchQuery })
        setDataModelSchemas(result || [])
      } catch (error) {
        console.error('Error searching data model schemas:', error)
      } finally {
        setIsLoading(false)
      }
    }

    const timeoutId = setTimeout(searchDataModelSchemas, 300) // Debounce search
    return () => clearTimeout(timeoutId)
  }, [searchQuery, open])

  // Memoize the selected value
  const selectedValue = useMemo(() => {
    if (selected?.id && selected?.label) {
      // Find the actual DataModelSchemaList object from the loaded schemas
      return dataModelSchemas.find(schema => schema.id === selected.id) || null
    }
    return null
  }, [selected?.id, selected?.label, dataModelSchemas])

  const handleChange = (event: any, newValue: DataModelSchemaList | null) => {
    onSelect(newValue)
  }

  const handleInputChange = (event: any, newInputValue: string) => {
    setSearchQuery(newInputValue)
  }

  return (
    <Autocomplete
      value={selectedValue}
      onChange={(event, newValue) => onSelect(newValue as DataModelSchemaList | null)}
      onInputChange={handleInputChange}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={dataModelSchemas}
      getOptionLabel={option => {
        if (typeof option === 'string') return option
        return option.description || option.id
      }}
      isOptionEqualToValue={(option, value) => option.id === value?.id}
      loading={isLoading}
      disabled={disabled}
      multiple={false}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          required={required}
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            startAdornment: selectedValue?.engine ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 1 }}>
                <ContentTypeTag contentType={selectedValue.engine} size="small" />
              </Box>
            ) : undefined,
            endAdornment: (
              <>
                {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props}>
          <Box>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {option.description || option.id}
            </Typography>
            {option.id && option.description && option.id !== option.description && (
              <Typography variant="caption" color="text.secondary">
                ID: {option.id}
              </Typography>
            )}
          </Box>
        </Box>
      )}
      noOptionsText={isLoading ? 'Loading...' : 'No data models found'}
      loadingText="Loading data models..."
      sx={{
        '& .MuiAutocomplete-popper': {
          zIndex: Z_INDEX.SELECT_MENU,
        },
      }}
      PaperComponent={({ children, ...other }) => (
        <Paper
          {...other}
          sx={{
            zIndex: Z_INDEX.SELECT_MENU,
            '& .MuiAutocomplete-listbox': {
              maxHeight: 300,
            },
            boxShadow: 3,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          {children}
        </Paper>
      )}
    />
  )
}
